import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import DraggableItem from "Components/Rearrange";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { updateRearrangeOrder } from "../../Redux/Slices/User/rearrange";
import "./style.scss";

const RearrangementQuestion = ({
  questionData,
  savedAnswers: savedAnswersProp = [],
  onNotesChange,
  notes: parentNotes,
  categoryIndex,
  subCategoryIndex,
  questionIndex,
}) => {
  const dispatch = useDispatch();

  // Prefer reading directly from Redux for the single source of truth
  const storeAnswers = useSelector((state) => {
    if (!questionData) return null;
    const id = questionData.question_id;
    const arr = state.rearrange?.answersByQuestion?.[id];

    return Array.isArray(arr) && arr.length ? arr : null;
  });


  const enrich = (arr = []) => {
    if (!questionData?.options) return arr;
    return arr.map((ans, idx) => {
      const opt = questionData.options.find((o) => o.option_id === ans.option_id);
      return {
        ...opt,
        ...ans,
        title: opt?.title ?? ans.title ?? "",
        option_id: ans.option_id ?? opt?.option_id ?? `missing-${idx}`,
      };
    });
  };


  const [items, setItems] = useState(() => {
  
    const initialSource = storeAnswers ?? (savedAnswersProp.length ? savedAnswersProp : questionData?.options ?? []);
    return enrich(initialSource);
  });

  // keep items in sync when storeAnswers or questionData changes
  useEffect(() => {
    const source = storeAnswers ?? (savedAnswersProp.length ? savedAnswersProp : questionData?.options ?? []);
    setItems(enrich(source));
  }, [storeAnswers, savedAnswersProp, questionData]);

  // notes
  const [localNotes, setLocalNotes] = useState(parentNotes || "");
  useEffect(() => {
    setLocalNotes(parentNotes || "");
  }, [parentNotes]);

  const handleNotesChange = (e) => {
    setLocalNotes(e.target.value);
    onNotesChange(categoryIndex, subCategoryIndex, questionIndex, "notes", e);
  };


  const dispatchTimer = useRef(null);
  const debouncedDispatch = (questionId, updatedItems) => {
    if (dispatchTimer.current) clearTimeout(dispatchTimer.current);
  
    dispatchTimer.current = setTimeout(() => {
      dispatch(
        updateRearrangeOrder({
          questionId,
          newOrder: updatedItems.map((it) => ({ ...it })),
        })
      );
      dispatchTimer.current = null;
    }, 120);
  };


  const moveItem = (fromIndex, toIndex) => {
    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);

      const withRanks = updated.map((it, i) => ({ ...it, rank_id: i + 1 }));

      // dispatch (debounced) to keep Redux in sync
      if (questionData?.question_id != null) {
        debouncedDispatch(questionData.question_id, withRanks);
      }

      return withRanks;
    });
  };

  if (!questionData) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="question-item mb-4">
        <h4>
          {questionData?.question_id}. {questionData?.question}
        </h4>

        {items.map((item, index) => (
          <DraggableItem
            key={`${questionData.question_id}-${item?.option_id ?? index}`}
            index={index}
            item={item?.title}
            moveItem={moveItem}
          />
        ))}

        <div className="option-items mt-3">
          <h4>Add your notes</h4>
          <textarea
            placeholder="Enter here..."
            className="notes-input"
            value={localNotes}
            onChange={handleNotesChange}
          />
        </div>
      </div>
    </DndProvider>
  );
};

export default React.memo(RearrangementQuestion);
